// Species presets — pure data, no game logic.
// Each SpeciesId maps to a CreatureRecipe (creature-recipe.ts) that COMPILES to
// the morphology floats driving the creature.slang feature SDF library. The
// seed field is randomised per instance at spawn time.

import type { Entity } from './components'
import { BIOMES, type BiomeId } from './biomes'
import { FEAT, morphologyFrom, type CreatureRecipe, type CreatureBehaviorKind } from './creature-recipe'
import { weightedPick } from './weighted'

// Re-exported for existing importers; behavior is body-coupled now.
export type { CreatureBehaviorKind }

// Mirror of FEAT_TEAM_HALO (bit 8): set on recruited allies so the creature
// shader draws a soft green outer aura — the "on your team" read. Re-exported
// from the canonical FEAT table for the ally/collision systems that toggle it.
export const FEAT_TEAM_HALO = FEAT.TEAM_HALO

export type SpeciesId =
  | 'ciliate'          // microbe — cilia ring
  | 'flagellate'       // microbe — whip tail(s)  [visual: Step 4]
  | 'spore'            // microbe — dense interior, no appendages  [visual: Step 4]
  | 'bristled'         // microbe — short outward tendrils  [visual: Step 4]
  | 'rod'              // microbe — pill body, darts and flees
  | 'spiral'           // microbe — spirillum, homes toward the player
  | 'plantCell'        // microbe — sessile DNA vault, cracked open
  | 'toadstool'        // mushroom-floor food — cap + stalk + gills
  | 'pseudopod'        // predator — angular lobes  [visual: Step 4]
  | 'spiked'           // predator — edge spikes  [visual: Step 4]
  | 'ciliateFriend'    // recruitable — cilia ring (green tint)
  | 'flagellateFriend' // recruitable — whip tail  [visual: Step 4]

// Species as composed parts. flagella/nucleusBig ignore `count` (tail count is
// seed-derived), so those rows carry no count.
const SPECIES_RECIPES: Record<SpeciesId, CreatureRecipe> = {
  ciliate:          { body: 'grain', ornaments: ['cilia'],      count: 18 },
  flagellate:       { body: 'grain', ornaments: ['flagella'] },
  spore:            { body: 'grain', ornaments: ['nucleusBig'] },
  bristled:         { body: 'grain', ornaments: ['tendrils'],   count: 8 },
  rod:              { body: 'rod', length: 0.3 },
  spiral:           { body: 'spiral', count: 3 },
  plantCell:        { body: 'plantCell', ornaments: ['cellWall', 'valueGlint'] }, // gold = value
  toadstool:        { body: 'mushroom', ornaments: ['gills'] },
  pseudopod:        { body: 'star',  count: 6,  length: 0.45 }, // 6 thick needles
  spiked:           { body: 'star',  count: 12, length: 0.62 }, // 12 long needles
  ciliateFriend:    { body: 'blob',  ornaments: ['cilia'],      count: 14 }, // fewer — friend radius larger
  flagellateFriend: { body: 'blob',  ornaments: ['flagella'] },
}

// Apex threat class (monorail-228): predator species that stay dangerous at
// any player size — they never flee, so a maxed-out run still has teeth. The
// spiked predator (12 long needles) is the natural "keep away" silhouette; it
// carries the apex flag. pseudopod stays a normal, flee-when-outgrown predator
// so apexes are a threatening SUBSET, not every predator. Spawn wires this onto
// the entity's predator component (spawn-predators.ts) and paints the apex tint.
const APEX_SPECIES: ReadonlySet<SpeciesId> = new Set<SpeciesId>(['spiked'])

export function isApexSpecies(id: SpeciesId): boolean {
  return APEX_SPECIES.has(id)
}

// Biome-weighted spawn pools live in BIOMES (biomes.ts) — one row per biome.

const FRIEND_WEIGHTS: [SpeciesId, number][] = [
  ['ciliateFriend',    50],
  ['flagellateFriend', 50],
]

export function pickMicrobeSpecies(biome: BiomeId): SpeciesId {
  return weightedPick(BIOMES[biome].microbeWeights, e => e[1])[0]
}

export function pickPredatorSpecies(biome: BiomeId): SpeciesId {
  return weightedPick(BIOMES[biome].predatorWeights, e => e[1])[0]
}

export function pickFriendSpecies(): SpeciesId {
  return weightedPick(FRIEND_WEIGHTS, e => e[1])[0]
}

export function morphologyFromSpecies(id: SpeciesId): Entity['morphology'] {
  return morphologyFrom(SPECIES_RECIPES[id], Math.random())
}

/** Recipe lookup for the gallery / tests (which need the packed floats). */
export function recipeForSpecies(id: SpeciesId): CreatureRecipe {
  return SPECIES_RECIPES[id]
}

/** All species recipes — for tests that validate the whole authored set. */
export function allSpeciesRecipes(): CreatureRecipe[] {
  return Object.values(SPECIES_RECIPES)
}
